import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api.js';
import { authClient } from '../auth-client.js';
import { GUEST_SESSION_KEY } from '../lib/useActor.js';
import Avatar from './Avatar.js';
import styles from './AvatarUploader.module.css';

/** 8MB, matching `MAX_AVATAR_UPLOAD_BYTES` server-side — checked here too so a phone photo over the cap fails instantly instead of after an upload. */
const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

/**
 * Forces Better Auth's client to refetch the session and update the shared
 * store every `useSession()` caller reads from. Needed because the upload
 * goes through our own `/api/account/avatar` route, not Better Auth's
 * `updateUser` — without this, `AppHeader` keeps rendering the previous
 * (already-deleted) photo URL until a full page reload.
 */
function refreshSession(): Promise<unknown> {
  return authClient.getSession({ query: { disableCookieCache: true } });
}

/**
 * Upload/replace/remove the signed-in account's own profile picture
 * (PLAN.md 10.5). Shared by the settings screen and a participant's own
 * profile page, so "change my photo" is reachable from where a person
 * actually looks at their photo, not only from settings.
 *
 * `onUploaded` lets the host screen refresh whatever it renders the photo
 * from — the profile screen reads it from its own API query, not from the
 * session, so it needs the nudge.
 */
export default function AvatarUploader({
  name,
  initialImageUrl,
  size = 72,
  stacked = false,
  onUploaded,
}: {
  name: string;
  initialImageUrl: string | null;
  size?: number;
  /** Actions below the avatar rather than beside it — for a header that already has a name sitting next to the photo. */
  stacked?: boolean;
  onUploaded?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The host screen's own data can arrive (or change) after this mounts.
  useEffect(() => setImageUrl(initialImageUrl), [initialImageUrl]);

  const run = async (action: () => Promise<string | null>) => {
    setError(null);
    setUploading(true);
    try {
      setImageUrl(await action());
      await refreshSession();
      // A guest has no Better Auth session for `refreshSession` to update, so
      // the header would keep rendering their old photo. Their identity comes
      // from this query instead (`lib/useActor.ts`); nudging it costs an
      // account holder one cheap request and nothing else.
      await queryClient.invalidateQueries({ queryKey: GUEST_SESSION_KEY });
      onUploaded?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings.avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file: File) => {
    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('settings.avatarTooLarge'));
      return;
    }
    void run(async () => (await api.uploadAvatar(file)).image);
  };

  return (
    <div className={`${styles.uploader} ${stacked ? styles.uploaderStacked : ''}`}>
      <button
        type="button"
        className={styles.avatarButton}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        aria-label={t('settings.avatarUpload')}
      >
        <Avatar name={name} imageUrl={imageUrl} size={size} />
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        disabled={uploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) handleFile(file);
        }}
      />

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? t('settings.avatarUploading') : t('settings.avatarUpload')}
        </button>
        {imageUrl ? (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() =>
              void run(async () => {
                await api.removeAvatar();
                return null;
              })
            }
            disabled={uploading}
          >
            {t('settings.avatarRemove')}
          </button>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
      </div>
    </div>
  );
}
